import { Instance, SnapshotIn, types } from 'mobx-state-tree'
import { parseBackendUtcDateString } from 'utils/datetimeUtils'

export enum ProfileUpdateRequestStatus {
    Requested = 'requested',
    Started = 'started',
    Updating = 'updating',
    Completed = 'completed',
    Failed = 'failed',
    Timeout = 'timeout',
    Canceled = 'canceled',
    FactoryInit = 'factory_init',
    FactoryInitComplete = 'factory_init_complete',
    FactoryInitFailed = 'factory_init_failed',
}

// As in oversight's ProfileUpdateRequest.ACTIVE_STATUSES and ENDED_STATUSES
const ACTIVE_STATUSES = [
    ProfileUpdateRequestStatus.Requested,
    ProfileUpdateRequestStatus.Started,
    ProfileUpdateRequestStatus.Updating,
    ProfileUpdateRequestStatus.FactoryInit,
]
const ENDED_STATUSES = [
    ProfileUpdateRequestStatus.Completed,
    ProfileUpdateRequestStatus.Failed,
    ProfileUpdateRequestStatus.Timeout,
    ProfileUpdateRequestStatus.FactoryInitComplete,
    ProfileUpdateRequestStatus.FactoryInitFailed,
]
const SUCCEEDED_STATUSES = [ProfileUpdateRequestStatus.Completed, ProfileUpdateRequestStatus.FactoryInitComplete]
const FAILED_STATUSES = [ProfileUpdateRequestStatus.Failed, ProfileUpdateRequestStatus.Timeout, ProfileUpdateRequestStatus.FactoryInitFailed]

const isStatusIn = (statuses: ReadonlyArray<ProfileUpdateRequestStatus>, status: string) =>
    (statuses as ReadonlyArray<string>).includes(status)

export const isKnownStatus = (status: string) => isStatusIn(Object.values(ProfileUpdateRequestStatus), status)

export const ProfileUpdateRequestModel = types.model('ProfileUpdateRequestModel', {
    id: types.identifierNumber,
    vehicle_id: types.number,
    profile_id: types.string,
    // A ProfileUpdateRequestStatus. Not an enumeration, so a status added in oversight doesn't fail the whole list:
    // requests with an unknown status are filtered out by ProfileUpdateRequestsStore.
    status: types.string,
    message: types.maybeNull(types.string),
    // Latest of the request's timestamps (request, start, end, heartbeat)
    timestamp: types.string,
})
.views((self) => ({
    get timestampDate(): Date {
        return parseBackendUtcDateString(self.timestamp)
    },
    get isActive(): boolean {
        return isStatusIn(ACTIVE_STATUSES, self.status)
    },
    get isEnded(): boolean {
        return isStatusIn(ENDED_STATUSES, self.status)
    },
    // Ended requests can be dismissed, and dismissing a request that wasn't started yet cancels it
    get isDismissible(): boolean {
        return isStatusIn(ENDED_STATUSES, self.status) || self.status === ProfileUpdateRequestStatus.Requested
    },
    get isSucceeded(): boolean {
        return isStatusIn(SUCCEEDED_STATUSES, self.status)
    },
    get isFailed(): boolean {
        return isStatusIn(FAILED_STATUSES, self.status)
    },
    // e.g. "Factory init failed"
    get statusText(): string {
        const text = self.status.replaceAll('_', ' ')
        return text.charAt(0).toUpperCase() + text.slice(1)
    },
}))

export type ProfileUpdateRequest = Instance<typeof ProfileUpdateRequestModel>
export type ProfileUpdateRequestSnapshotIn = SnapshotIn<typeof ProfileUpdateRequestModel>
export type ProfileUpdateRequestCreatePayload = Pick<ProfileUpdateRequestSnapshotIn, 'vehicle_id' | 'profile_id'>
export type ProfileUpdateRequestCreateResponse = Pick<ProfileUpdateRequestSnapshotIn, 'id'>
